import { z } from "zod";

const ErrorReasonSchema = z.object({ reason: z.string() });
const RecordCountSchema = z.object({ record_count: z.number() });

const ValidationIssuesSchema = z.object({
  issues: z.array(z.object({
    path: z.array(z.union([z.string(), z.number()])),
    message: z.string(),
  })),
});
const PermissionDataSchema = z.object({ resource: z.string(), action: z.string() });

export type FailedResult = { message: string; data: unknown };
export type CrmValidationIssue = z.infer<typeof ValidationIssuesSchema>["issues"][number];
type ReasonMessage = string | ((data: unknown) => string);

/** The `data.reason` of a failed CRM response, if it has one. */
export function crmErrorReason(data: unknown): string | null {
  const parsed = ErrorReasonSchema.safeParse(data);
  return parsed.success ? parsed.data.reason : null;
}

/** The body validation issues of a 400 response (`data.issues`), if it has them. */
export function crmValidationIssues(data: unknown): CrmValidationIssue[] | null {
  const parsed = ValidationIssuesSchema.safeParse(data);
  return parsed.success ? parsed.data.issues : null;
}

/**
 * Whether a failed response is an auth or permission failure (401/403). The response status isn't
 * exposed, so this matches the general API's auth guard bodies.
 */
export function isCrmAuthError(result: FailedResult): boolean {
  return PermissionDataSchema.safeParse(result.data).success
    || crmErrorReason(result.data) === "internal_account_required"
    || result.message === "Unauthenticated"
    || result.message === "Forbidden";
}

/** A readable message for a failed CRM response, chosen by its `data.reason`, else the server message. */
function describeCrmError(
  result: FailedResult,
  messagesByReason: Record<string, ReasonMessage>,
  fallback: string,
): string {
  const reason = crmErrorReason(result.data);
  const message = reason ? messagesByReason[reason] : undefined;
  if (typeof message === "function") return message(result.data);
  return message ?? (result.message || fallback);
}

const moveRecordMessages: Record<string, ReasonMessage> = {
  crm_record_revision_conflict:
    "This record was changed by someone else. The page has been refreshed, so try again.",
  crm_record_move_subtree_changed:
    "A record under this one changed during the move. The page has been refreshed, so try again.",
  crm_record_move_owned_record:
    "This record belongs to another record, so it can't be moved on its own. Move the record it belongs to instead.",
  crm_record_move_same_master: "This record is already in that master record.",
  crm_record_not_found: "This record no longer exists.",
  crm_record_move_target_master_not_found: "That master record no longer exists. Choose another one.",
};

const deleteMasterMessages: Record<string, ReasonMessage> = {
  crm_master_revision_conflict:
    "This master record was changed by someone else. The page has been refreshed, so check it and try again.",
  crm_master_not_found: "This master record no longer exists.",
  crm_master_has_records: (data) => {
    const parsed = RecordCountSchema.safeParse(data);
    const count = parsed.success ? parsed.data.record_count : null;
    const records = count === null ? "records" : `${count.toLocaleString()} record${count === 1 ? "" : "s"}`;
    // Removed records still count, so this can happen even when none are shown.
    return `This master record still has ${records}, including any removed ones. Move them to another master record first.`;
  },
};

/** For POST /crm/developer/records/move-master. */
export function describeMoveRecordError(result: FailedResult): string {
  return describeCrmError(result, moveRecordMessages, "The record couldn't be moved. Try again.");
}

/** For POST /crm/developer/masters/:id/delete. */
export function describeDeleteMasterError(result: FailedResult): string {
  return describeCrmError(result, deleteMasterMessages, "The master record couldn't be deleted. Try again.");
}
